import { useTheme } from '@mui/material/styles';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { login, OAuthClientRegistrationError } from 'services/auth';
import useAlert from 'utils/alert';
import { logger } from 'utils/logger';
import { sendEmailAccessLink } from './services';

export type SignInSceneName = 'login' | 'lost';

export default function useSignInHooks() {
  const { t } = useTranslation();
  const theme = useTheme();
  const history = useHistory();
  const location = useLocation();
  const showAlert = useAlert();

  const [initialValues] = useState(() => {
    const params = new URLSearchParams(location.search);
    const email = params.get('email') || '';
    const secret = params.get('secret') || '';
    return email && secret ? { email, secret } : { email: '', secret: '' };
  });
  const hasQueryParams = Boolean(initialValues.email && initialValues.secret);

  useEffect(() => {
    if (location.search && hasQueryParams) {
      history.replace('/sign-in');
    }
  }, [location.search, hasQueryParams, history]);

  const [loginScene, setLoginScene] = useState<SignInSceneName>(hasQueryParams ? 'lost' : 'login');
  const [oauthError, setOauthError] = useState(false);

  // On success login() navigates away (window.location.assign), so only failures return here.
  // The error renders inline in LoginForm (role="alert"); no extra toast to avoid a duplicate.
  const handleOAuthLogin = async () => {
    setOauthError(false);
    try {
      await login();
    } catch (err) {
      if (!(err instanceof OAuthClientRegistrationError)) logger.error(err);
      setOauthError(true);
    }
  };

  // Success renders inline in LoginForm (sent state); only failures toast.
  const handleSendEmailLink = async ({ email }: { email: string }): Promise<boolean> => {
    try {
      await sendEmailAccessLink(email);
      return true;
    } catch (err) {
      showAlert(t('auth:error-try-again'), 'error');
      return false;
    }
  };

  const handleChangeScene = (scene: SignInSceneName) => {
    setLoginScene(scene);
  };

  return {
    handleOAuthLogin,
    handleSendEmailLink,
    handleChangeScene,
    loginScene,
    initialValues,
    oauthError,
    t,
    theme,
    history,
  };
}
